import { useState, useRef, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import posService from "../../services/pos/pos.service";

interface InwardDialogProps {
  visible: boolean;
  onHide: () => void;
  product: any;
  onSuccess: (posId: number[]) => void;
  setLoading: (loading: boolean) => void;
}

interface SourceType {
  id: number;
  name: string;
}

interface VendorDetails {
  vendorID: number;
  firmInfo: string;
}

const InwardProduct: React.FC<InwardDialogProps> = ({ visible, onHide, product, onSuccess, setLoading }) => {
  const toast = useRef<Toast>(null);

  const [quantity, setQuantity] = useState<number>(0);
  const [selectedSourceType, setSelectedSourceType] = useState<SourceType | null>(null);
  const [sourceTypes, setSourceTypes] = useState<SourceType[]>([]);
  const [vendor, setVendor] = useState<VendorDetails | null>(null);
  const [vendorList, setVendorList] = useState<VendorDetails[]>([]);
  const [batchCode, setBatchCode] = useState<string>("");
  const [qualityCheck, setQualityCheck] = useState<boolean>(false);
  const [note, setNote] = useState<string>("");


  const loadSourceTypeDropdown = async () => {
    try {
      const sourcetyperesponse = await posService.GetinwardType();
      if (Array.isArray(sourcetyperesponse)) {
        const filteredSourceTypes = sourcetyperesponse.filter((type: SourceType) => type.id !== 4);
        setSourceTypes(filteredSourceTypes);
      }
      const vendorsResponse = await posService.GetAllVendors();
      if (Array.isArray(vendorsResponse)) {
        const formattedVendors = vendorsResponse.map((v: VendorDetails) => ({
          firmInfo: v.firmInfo,
          vendorID: v.vendorID,
        }));
        setVendorList(formattedVendors);
      }
    } catch (error) {
      console.error("Failed to load dropdown data", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not load dropdown data",
      });
    }
  };

  useEffect(() => {
    if (visible) {
      loadSourceTypeDropdown();
    }
    if (!visible) {
      setQuantity(0);
      setSelectedSourceType(null);
      setVendor(null);
      setBatchCode("");
      setQualityCheck(false);
      setNote("");
    }
  }, [visible]);

  const handleSubmit = async () => {

    if (!quantity || quantity <= 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please enter a valid quantity.",
      });
      return;
    }

    if (!selectedSourceType) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select a source type.",
      });
      return;
    }

    if (!vendor) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select a vendor.",
      });
      return;
    }

    if (!batchCode.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please enter a batch code or document.",
      });
      return;
    }
    if (product.measurementTypeId === 4 && !Number.isInteger(quantity)) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Decimal quantities are not allowed for this product.",
      });
      return;
    }

    const payload = {
      ProductId: product.productId,  // from prop
      Posid: product.posId,           // assuming product.posId exists, else adjust accordingly
      Quantity: quantity,
      SourceTypeId: selectedSourceType?.id,
      VendorId: vendor?.vendorID,
      FromPosid: 0,                  // pass 0 or null for empty FromPosid as per your API
      DocumentDetails: batchCode,
      QualityCheck: qualityCheck,
      Note: note
    };

    try {
      setLoading(true);
      const response = await posService.AddPosInwardproducts(payload);
      if (response && response.isSuccess) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message || "Inward data submitted successfully",
        });
        onHide(); // Close modal on success
        onSuccess([product.posId]);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: response?.message || "Failed to submit inward data",
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "An error occurred during submission",
      });
    } finally {
      setLoading(false); // Always stop loading
    }
  };


  return (
    <>
      <Toast ref={toast} />
      <Dialog header="Inward Product" visible={visible} onHide={onHide} breakpoints={{ '960px': '90vw' }} style={{ width: '30vw' }} modal>
        <div className="p-inputtext p-component bg-gray-100 py-2 px-3 rounded">
          {product?.productName || '-'}
        </div>
        <div className="p-fluid">
          <div className="mb-3">
            <label className="font-semibold text-sm block mb-1">Quantity <span className="text-danger">*</span></label>
            <InputText type="number" min="0" keyfilter="pnum" onChange={(e) => setQuantity(Number(e.target.value))}
              style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
            />
          </div>

          <div className="mb-3">
            <label className="font-semibold text-sm block mb-1">
              Source Type <span className="text-danger">*</span>
            </label>
            <Dropdown
              value={selectedSourceType}
              onChange={(e) => setSelectedSourceType(e.value)}
              options={sourceTypes}
              optionLabel="name"
              placeholder="Select a SourceType"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
          </div>

          <div className="mb-3">
            <label className="font-semibold text-sm block mb-1">
              Vendor <span className="text-danger">*</span>
            </label>
            <Dropdown
              value={vendor}
              options={vendorList}
              onChange={(e) => setVendor(e.value)}
              optionLabel="firmInfo"
              placeholder="Select vendor"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
          </div>

          <div className="mb-3">
            <label className="font-semibold text-sm block mb-1">
              Batch Code / Document Log <span className="text-danger">*</span>
            </label>
            <InputText
              value={batchCode}
              onChange={(e) => setBatchCode(e.target.value)}
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
          </div>


          <div className="mb-3 flex align-items-center">
            <Checkbox
              inputId="qc"
              checked={qualityCheck}
              onChange={(e) => setQualityCheck(e.checked!)}
            />
            <label htmlFor="qc" className="ml-2">Quality Check</label>
          </div>

          <div className="mb-3">
            <label className="font-semibold text-sm block mb-1">Note</label>
            <InputText value={note} onChange={(e) => setNote(e.target.value)}
              style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
              maxLength={255}
            />
            <small style={{ display: 'block', marginTop: '5px', color: '#6c757d' }}>
              {note.length} / 255
            </small>
          </div>

          <Button label="Submit" className="mt-2" onClick={handleSubmit} />
        </div>
      </Dialog>
    </>
  );
};

export default InwardProduct;
